<template>
  <Modal ref="modal" title="Vincular ao monitoramento" :force="loading" data-testid="vincular-monitoramento-modal">
    <div v-if="autoescola" class="vincular-container">
      <div class="instrutor">
        <span class="nome">{{ autoescola.nome_fantasia }}</span>
        <span class="cpf">CPF {{ autoescola.cnpj }}</span>
        <span v-if="autoescola.municipio_principal" class="cpf">
          {{ autoescola.municipio_principal }}<template v-if="autoescola.codigo_regional"> · Regional {{ autoescola.codigo_regional }}</template>
        </span>
      </div>

      <div v-if="autoescola.vinculado_monitoramento" class="aviso info">
        <InformationOutline :size="20" class="aviso-icon" />
        <span>
          Consta como vinculado desde <strong>{{ dataVinculo }}</strong>. Use esta opção só se o vínculo foi
          encerrado no Detran e precisa ser refeito.
        </span>
      </div>

      <div v-if="autoescola.vinculado_monitoramento && !autoescola.municipio_principal" class="aviso alerta">
        <AlertCircleOutline :size="20" class="aviso-icon" />
        <span>
          Vínculo feito antes da exigência do município principal. Se o instrutor não consegue emitir LADV, o
          município precisa ser ajustado pelo próprio Detran.
        </span>
      </div>

      <p class="descricao">
        O instrutor será associado à nossa empresa de monitoramento no Detran. Esse vínculo é necessário para
        cadastrar veículos e emitir LADV.
      </p>

      <div class="municipio">
        <Select :options="optionsMunicipios" legend="Município principal de atuação" placeholder="Selecione o município"
          :modelValue="municipio" @update:modelValue="$event => (municipio = $event)" :disabled="loading"
          data-testid="select-municipio-principal" />

        <small>
          Define a regional do instrutor na emissão de LADV. Se ele já tiver município no Detran, o cadastrado é
          mantido; depois, só o Detran altera.
        </small>
      </div>

      <div class="aviso alerta">
        <AlertCircleOutline :size="20" class="aviso-icon" />
        <span>
          O Detran permite apenas <strong>um vínculo ativo</strong> por instrutor. Se ele já estiver vinculado a
          outra empresa, a solicitação será recusada e a troca precisa ser feita no próprio Detran.
        </span>
      </div>

      <div class="actions">
        <Button type="button" text="Cancelar" color="danger" :fill="false" :disabled="loading" @handle:click="close" />
        <Button type="button" :text="loading ? 'Vinculando...' : 'Vincular no Detran'" color="primary"
          :disabled="loading || !municipio" @handle:click="vincular" data-testid="btn-confirmar-vinculo">
          <LinkVariantPlus :size="18" />
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from 'core/Modal'
import Button from 'core/Button'
import Select from 'core/Select'
import InformationOutline from 'icons/InformationOutline'
import AlertCircleOutline from 'icons/AlertCircleOutline'
import LinkVariantPlus from 'icons/LinkVariantPlus'
import utils from '../../services/utils'
import { MUNICIPIOS_CE_OPTIONS, encontrarMunicipioCE } from '../../constants/municipiosCE'

export default {
  name: 'VincularMonitoramentoModal',
  components: {
    Modal,
    Button,
    Select,
    InformationOutline,
    AlertCircleOutline,
    LinkVariantPlus,
  },
  data() {
    return {
      autoescola: null,
      municipio: '',
      optionsMunicipios: MUNICIPIOS_CE_OPTIONS,
      loading: false,
    }
  },
  computed: {
    dataVinculo() {
      const data = this.autoescola && this.autoescola.vinculado_monitoramento_em
      // Laravel 6 serializa como "Y-m-d H:i:s"
      return data ? utils.formatOnlyDate(String(data).slice(0, 10)) : '-'
    },
  },
  methods: {
    open(autoescola) {
      this.autoescola = autoescola
      // Sugestão: município já gravado ou a cidade no fim do endereço ("logradouro, bairro, cidade")
      const cidadeEndereco = (autoescola.endereco || '').split(',').pop()
      this.municipio = encontrarMunicipioCE(autoescola.municipio_principal) || encontrarMunicipioCE(cidadeEndereco)
      this.$refs.modal.open()
    },
    close() {
      this.$refs.modal.close()
    },
    async vincular() {
      this.loading = true

      await this.$http.post(
        `${process.env.VUE_APP_URL_API_V2}/getran/autoescola/${this.autoescola.id_cfc}/empresa-monitoramento`,
        { municipio: this.municipio }
      )
        .then(response => {
          this.toast(response.body.mensagem, 'success')
          this.$emit('vinculado', response.body.dados.autoescola)
          this.close()
        })
        .catch(err => {
          this.toast((err.body && err.body.mensagem) || 'Erro ao vincular o instrutor no Detran.')
        })
        .finally(() => {
          this.loading = false
        })
    },
    toast(text, type = 'error') {
      this.$toasted.show(text, {
        icon: type === 'success' ? 'fa-check-circle' : 'fa-times',
        position: 'top-right',
        duration: 6000,
        fullWidth: false,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.vincular-container {
  // O Modal core não limita a largura: desconta o padding dele (2rem de cada lado) e uma margem
  width: 32rem;
  max-width: calc(100vw - 5.5rem);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .instrutor {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 1rem;
    border: 1px solid #ececec;
    border-radius: 0.5rem;

    .nome {
      font-weight: 700;
      color: #1c2d5a;
      overflow-wrap: anywhere;
    }

    .cpf {
      font-size: 0.875rem;
      color: #606264;
    }
  }

  .municipio {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    small {
      font-size: 0.8125rem;
      line-height: 1.4;
      color: #606264;
    }
  }

  .descricao {
    margin: 0;
    font-size: 0.875rem;
    color: #606264;
  }

  .aviso {
    display: flex;
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 0.5rem;
    font-size: 0.8125rem;
    line-height: 1.4;

    .aviso-icon {
      flex-shrink: 0;
    }

    &.info {
      background-color: rgba(28, 45, 90, 0.05);
      color: #1c2d5a;
    }

    &.alerta {
      background-color: rgba(255, 193, 7, 0.12);
      color: #7a5b00;
    }
  }

  .actions {
    align-self: flex-end;
    display: flex;
    gap: 1rem;
  }

  @media (max-width: 576px) {
    .actions {
      align-self: stretch;
      flex-direction: column-reverse;
    }
  }
}
</style>
